import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { t } from '../../lib/i18n';
import { Users, Plus, Check, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';

const API_URL = '/api';

/**
 * FactionSelect - Autocomplete for faction field that links to lore articles of type "faction".
 * Shows existing factions, allows free text, and offers to create a new faction article.
 */
export function FactionSelect({ value, onChange, campaignId, language, placeholder, className, id, disabled }) {
  const [open, setOpen] = useState(false);
  const [factions, setFactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch faction lore articles
  const fetchFactions = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/lore`);
      const factionArticles = response.data
        .filter(a => a.article_type === 'faction')
        .map(a => ({ id: a.id, title: a.title, summary: a.summary }));
      setFactions(factionArticles);
    } catch (error) {
      console.error('Failed to load factions');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchFactions();
  }, [fetchFactions]);

  // Sync external value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredFactions = factions.filter(f =>
    !inputValue || f.title.toLowerCase().includes(inputValue.toLowerCase())
  );

  const exactMatch = factions.some(f => f.title.toLowerCase() === inputValue.toLowerCase());

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    if (!open) setOpen(true);
  };

  const handleSelect = (factionTitle) => {
    setInputValue(factionTitle);
    onChange(factionTitle);
    setOpen(false);
  };

  const handleCreateFaction = () => {
    setOpen(false);
    navigate(`/campaigns/${campaignId}/lore?type=faction`, {
      state: { createFaction: true, factionName: inputValue }
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "pr-8",
            className
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => { setOpen(!open); inputRef.current?.focus(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
          <div className="max-h-[200px] overflow-y-auto p-1">
            {loading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {language === 'fr' ? 'Chargement...' : 'Loading...'}
              </div>
            ) : filteredFactions.length === 0 && !inputValue ? (
              <div className="py-3 px-2 text-center text-sm text-muted-foreground">
                {language === 'fr' ? 'Aucune faction créée' : 'No factions created'}
              </div>
            ) : (
              <>
                {filteredFactions.map((faction) => (
                  <button
                    key={faction.id}
                    type="button"
                    onClick={() => handleSelect(faction.title)}
                    className={cn(
                      "flex items-center gap-2 w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      inputValue === faction.title && "bg-accent/50"
                    )}
                  >
                    <Users className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                    <div className="flex-1 text-left min-w-0">
                      <span className="font-medium truncate block">{faction.title}</span>
                      {faction.summary && (
                        <span className="text-xs text-muted-foreground truncate block">{faction.summary}</span>
                      )}
                    </div>
                    {inputValue === faction.title && (
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                    )}
                  </button>
                ))}

                {inputValue && !exactMatch && (
                  <div className="text-xs text-muted-foreground px-2 py-1 mt-1 border-t border-border/50">
                    {language === 'fr'
                      ? `"${inputValue}" sera utilisé comme texte libre`
                      : `"${inputValue}" will be used as free text`}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Create faction button */}
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={handleCreateFaction}
              className={cn(
                "flex items-center gap-2 w-full rounded-sm px-2 py-2 text-sm cursor-pointer transition-colors",
                "hover:bg-primary/10 text-primary"
              )}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 text-left font-medium">
                {language === 'fr' ? 'Créer une nouvelle faction' : 'Create a new faction'}
              </span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-50" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
